import { useMemo } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchProjects } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { ViewSwitcher } from "@/components/ViewSwitcher";
import { FilterBar } from "@/components/FilterBar";
import { Filmstrip } from "@/components/Filmstrip";
import { GridView } from "@/components/GridView";
import { ListView } from "@/components/ListView";
import { DemoBanner } from "@/components/DemoBanner";

export default function Home({ defaultType }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { data, isLoading, isError } = useQuery({ queryKey: ["projects"], queryFn: fetchProjects });

  const view = params.get("view") || "slider";
  const category = params.get("category") || "all";
  const type = params.get("type") || defaultType || "all";
  const search = params.get("q") || "";

  const setView = (v) =>
    setParams(
      (p) => {
        p.set("view", v);
        return p;
      },
      { replace: true }
    );
  const setCategory = (c) =>
    setParams(
      (p) => {
        if (c === "all") p.delete("category");
        else p.set("category", c);
        return p;
      },
      { replace: true }
    );
  const setType = (t) =>
    setParams(
      (p) => {
        if (t === "all") p.delete("type");
        else p.set("type", t);
        return p;
      },
      { replace: true }
    );
  const setSearch = (q) =>
    setParams(
      (p) => {
        if (!q) p.delete("q");
        else p.set("q", q);
        return p;
      },
      { replace: true }
    );
  const clearAll = () =>
    setParams(
      (p) => {
        p.delete("category");
        p.delete("type");
        p.delete("q");
        return p;
      },
      { replace: true }
    );

  const allProjects = data || [];

  const categories = useMemo(() => {
    const map = new Map();
    allProjects.forEach((p) => map.set(p.category, (map.get(p.category) || 0) + 1));
    return Array.from(map, ([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allProjects]);

  const types = useMemo(() => {
    const map = new Map();
    allProjects.forEach((p) => map.set(p.productionType, (map.get(p.productionType) || 0) + 1));
    return Array.from(map, ([name, count]) => ({ name, count }));
  }, [allProjects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allProjects.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (type !== "all" && p.productionType !== type) return false;
      if (q) {
        const hay = [p.title, p.brand, p.category, p.productionType, ...(p.keywords || [])].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allProjects, category, type, search]);

  return (
    <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid="home-page">
      <DemoBanner />
      <Nav rightSlot={<ViewSwitcher view={view} onChange={setView} />} />
      <FilterBar
        categories={categories}
        types={types}
        activeCategory={category}
        activeType={type}
        search={search}
        onCategory={setCategory}
        onType={setType}
        onSearch={setSearch}
        onClear={clearAll}
        totalCount={allProjects.length}
      />
      {isLoading && (
        <div className="flex items-center justify-center h-[50vh] font-mono text-xs uppercase tracking-widest text-[#555550]" data-testid="home-loading-state">
          Loading collection…
        </div>
      )}
      {isError && (
        <div className="flex items-center justify-center h-[50vh] font-mono text-xs uppercase tracking-widest text-[#888880]" data-testid="home-error-state">
          Could not load the collection. Please retry shortly.
        </div>
      )}
      {!isLoading && !isError && (
        <>
          {view === "slider" && (
            <div className="pt-6 sm:pt-8">
              <Filmstrip projects={filtered} searchString={location.search} />
            </div>
          )}
          {view === "grid" && <GridView projects={filtered} searchString={location.search} />}
          {view === "list" && <ListView projects={filtered} searchString={location.search} />}
        </>
      )}
    </div>
  );
}
